import type { IncomingMessage } from 'http';
import type { ParsedUrlQuery } from 'querystring';
import { ShareType, ShareUrl } from '../shareTypes';
import type { ShareProps } from '../shareTypes';
import { fetchClassData, fetchDetailedWorkoutData, fetchInviteDetails } from './data';
import type { ShareMetaTagType } from './html';
import {
  toClassTags,
  toDetailedWorkoutTags,
  toInviteDetailsTags,
  toGenericWorkoutTags,
  toScheduleEventDetailsTags,
  toTagTags,
} from './og';

export const toMetaTags = async <T extends ShareType>(
  shareType: ShareType,
  shareProps: ShareProps[T],
  req: IncomingMessage | undefined,
  query: ParsedUrlQuery,
  asPath: string | undefined,
) => {
  try {
    switch (shareType) {
      case ShareType.ClassDetails: {
        if (asPath?.includes(ShareUrl.Classes)) {
          const { classId } = shareProps as ShareProps['class-details'];
          // this route gets picked up by /classes/*
          // so member may not actually be sharing a class (hence no classId)
          if (!classId || !req) {
            return [] as ShareMetaTagType[];
          }

          return toClassTags(
            req,
            query,
            asPath,
            classId,
            await fetchClassData(req, query, classId),
          );
        }
        return [] as ShareMetaTagType[];
      }
      case ShareType.TagDetails: {
        if (asPath?.includes(ShareUrl.Tags)) {
          const { tagName } = shareProps as ShareProps['tag-details'];

          if (!tagName || !req) {
            return [] as ShareMetaTagType[];
          }

          return toTagTags(req!, query, asPath, tagName!);
        }
        return [] as ShareMetaTagType[];
      }
      case ShareType.ScheduleEventDetails: {
        if (asPath?.includes(ShareUrl.Schedule)) {
          const {
            classId,
            start,
            type,
          } = shareProps as ShareProps['schedule-event-details'];

          if (!classId || !start || !type || !req) {
            return [] as ShareMetaTagType[];
          }

          return toScheduleEventDetailsTags(
            req,
            query,
            asPath,
            start,
            await fetchClassData(req, query, classId),
          );
        }
        return [] as ShareMetaTagType[];
      }
      case ShareType.DetailedWorkoutDetails: {
        if (asPath?.includes(ShareUrl.ShareWorkouts)) {
          const { shareWorkoutId } = shareProps as ShareProps['detailed-workout-details'];
          if (!shareWorkoutId || !req) {
            return [] as ShareMetaTagType[];
          }

          return toDetailedWorkoutTags(
            req,
            query,
            asPath,
            shareWorkoutId,
            await fetchDetailedWorkoutData(req, query, shareWorkoutId),
          );
        }
        return [] as ShareMetaTagType[];
      }
      case ShareType.InviteDetails: {
        if (asPath?.includes(ShareUrl.Invite)) {
          const { inviteId } = shareProps as ShareProps['invite-details'];

          if (!inviteId || !req) {
            return [] as ShareMetaTagType[];
          }

          const inviteDetails = await fetchInviteDetails(req, query, inviteId);

          return toInviteDetailsTags(req, query, asPath, inviteDetails);
        }
        return [] as ShareMetaTagType[];
      }
      case ShareType.GenericWorkoutDetails: {
        if (
          asPath?.includes(ShareUrl.Members) ||
          asPath?.includes(ShareUrl.ProfileWorkouts)
        ) {
          const { workoutId } = shareProps as ShareProps['generic-workout-details'];

          if (!workoutId || !req) {
            return [] as ShareMetaTagType[];
          }

          return toGenericWorkoutTags(
            req,
            query,
            asPath,
            (shareProps as ShareProps['generic-workout-details']).workoutId,
          );
        }
        return [] as ShareMetaTagType[];
      }
      default:
        return [] as ShareMetaTagType[];
    }
  } catch (e) {
    return [] as ShareMetaTagType[];
  }
};
